/**
 * Fallback source for the interview buffer config used by self-schedule
 * wizards (incl. `self-schedule-adhoc`) when the wizard has no interview
 * plan to read it off. Normal self-schedule keeps reading the plan
 * response (`GET /ats_interviews/new` pre-fills from `AccountSetting`);
 * this fetch only fires when the plan path is empty.
 *
 * Wire shape — same as `_account_setting.json.jbuilder` (kula/core#4973):
 *   { interview_buffer_config: { enabled, pre_minutes, post_minutes } }
 *
 * Endpoint shared with `useAccountMinimumNotice` — `staleTime` aligned
 * so successive consumers reuse one cached response per render cycle.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';

export interface InterviewBufferConfig {
  enabled: boolean;
  pre_minutes: number;
  post_minutes: number;
}

export interface AccountBufferSettings {
  interview_buffer_config?: InterviewBufferConfig;
}

interface AccountBufferEnvelope {
  data?: AccountBufferSettings;
}

export const accountBufferQueryKey = [
  'schedule-interview',
  'account-buffer',
] as const;

export function useAccountBuffer(options?: {
  enabled?: boolean;
}): UseQueryResult<AccountBufferSettings, Error> {
  return useQuery({
    queryKey: accountBufferQueryKey,
    queryFn: async () => {
      const res = await http.get<AccountBufferEnvelope>(
        '/ats_schedules/settings'
      );
      return (res?.data ?? {}) as AccountBufferSettings;
    },
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}
